export type LocaleDirection = 'ltr' | 'rtl';

const LOCALE_REGISTRY = [
  {
    id: 'en',
    languageTag: 'en-US',
    direction: 'ltr',
    label: 'English',
    isDefault: true
  },
  {
    id: 'ar',
    languageTag: 'ar-SA',
    direction: 'rtl',
    label: 'العربية'
  }
] as const satisfies readonly {
  id: string;
  languageTag: string;
  direction: LocaleDirection;
  label: string;
  isDefault?: boolean;
}[];

export type LocaleDefinition = (typeof LOCALE_REGISTRY)[number];

export const SUPPORTED_LOCALES = LOCALE_REGISTRY;

export type LocaleCode = (typeof SUPPORTED_LOCALES)[number]['id'];

const configuredDefaultLocale = SUPPORTED_LOCALES.find(
  (locale) => (locale as { isDefault?: boolean }).isDefault === true
);

export const DEFAULT_LOCALE: LocaleCode = (configuredDefaultLocale ?? SUPPORTED_LOCALES[0]).id;

export const SUPPORTED_LOCALE_CODES: LocaleCode[] = SUPPORTED_LOCALES.map((locale) => locale.id);

export const RIGHT_TO_LEFT_LOCALES = SUPPORTED_LOCALES.filter(
  (locale) => locale.direction === 'rtl'
).map((locale) => locale.id);

export const LOCALE_HEADER_NAME = 'x-athwab-locale';
export const LOCALE_QUERY_PARAM = 'locale';

const normalizeLocaleInput = (value: string) => value.toLowerCase().replace(/_/g, '-').trim();

const findLocale = (value: string): LocaleDefinition | null => {
  const normalized = normalizeLocaleInput(value);

  const directMatch = SUPPORTED_LOCALES.find((locale) => locale.id === normalized);
  if (directMatch) {
    return directMatch;
  }

  const languageTagMatch = SUPPORTED_LOCALES.find(
    (locale) => locale.languageTag.toLowerCase() === normalized
  );
  if (languageTagMatch) {
    return languageTagMatch;
  }

  const [base] = normalized.split('-');
  if (!base) {
    return null;
  }

  const baseMatch = SUPPORTED_LOCALES.find((locale) => locale.id === base);
  return baseMatch ?? null;
};

export const isLocaleCode = (value: string | null | undefined): value is LocaleCode =>
  typeof value === 'string' && findLocale(value) !== null;

export const getLocaleByCode = (value: string | null | undefined): LocaleDefinition | null => {
  if (!value) {
    return null;
  }

  return findLocale(value);
};

export const resolveLocale = (value: string | null | undefined): LocaleDefinition => {
  return getLocaleByCode(value) ?? SUPPORTED_LOCALES[0];
};
